import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Slider,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Upload as LoadIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  TrendingUp as StatsIcon,
  Map as MapIcon,
  Download as ExportIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Types for our mineral discovery system
interface PresetConfiguration {
  id: string;
  name: string;
  description: string;
  commodity: 'gold' | 'silver' | 'copper';
  region: string;
  rules: string[];
  expectedResults: string;
}

interface RuleWeight {
  ruleName: string;
  weight: number;
  enabled: boolean;
  description: string;
}

interface AnalysisConfiguration {
  commodity: 'gold' | 'silver' | 'copper';
  region: string;
  ruleWeights: RuleWeight[];
  analysisArea: {
    bounds: [number, number, number, number]; // [minLat, minLon, maxLat, maxLon]
    gridResolution: number;
  };
  outputFormats: string[];
  confidenceThreshold: number;
  goldVeinAnalysis: boolean; // Enhanced gold vein detection
}

interface AnalysisStatus {
  id: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: number;
  message: string;
  estimatedTimeRemaining?: number;
  startTime?: Date;
}

const PRESET_CONFIGURATIONS: PresetConfiguration[] = [
  {
    id: 'gold_argentina',
    name: 'Gold Prospects - Argentina',
    description: 'Optimized for epithermal and orogenic gold deposits in Argentina',
    commodity: 'gold',
    region: 'Argentina',
    rules: ['sericite_alteration', 'gold_geochemical_anomaly', 'fault_proximity_gold'],
    expectedResults: 'High-grade epithermal gold prospects in structural corridors'
  },
  {
    id: 'gold_chile',
    name: 'Gold Prospects - Chile',
    description: 'Focused on porphyry-related gold systems in Chilean Andes',
    commodity: 'gold',
    region: 'Chile',
    rules: ['sericite_alteration', 'iron_oxide_alteration', 'fault_proximity_gold'],
    expectedResults: 'Porphyry-associated gold deposits in volcanic terranes'
  },
  {
    id: 'copper_porphyry',
    name: 'Copper Porphyry Systems',
    description: 'Comprehensive porphyry copper detection across South America',
    commodity: 'copper',
    region: 'South America',
    rules: ['porphyry_phyllic_alteration', 'porphyry_argillic_alteration', 'porphyry_iron_cap', 'porphyry_potassic_alteration'],
    expectedResults: 'Large-scale porphyry copper systems with spectral alteration signatures'
  },
  {
    id: 'silver_epithermal',
    name: 'Silver Epithermal Systems',
    description: 'High-grade epithermal silver deposits in volcanic arcs',
    commodity: 'silver',
    region: 'Andean Arc',
    rules: ['silver_geochemical_anomaly', 'clay_alteration', 'fault_proximity_gold'],
    expectedResults: 'High-grade epithermal silver veins in volcanic environments'
  },
  {
    id: 'daura_gold_peru',
    name: 'Daura Gold - Ancash Peru',
    description: 'Epithermal gold systems in Pucajirca volcanic center, Ancash Department',
    commodity: 'gold',
    region: 'Ancash_Peru',
    rules: ['peru_sericite_alteration', 'ancash_structural_control', 'pucajirca_volcanism', 'peru_gold_pathfinder'],
    expectedResults: 'High-grade epithermal gold veins in NW-SE structural corridors with Miocene volcanic hosts'
  }
];

const DEFAULT_RULE_WEIGHTS: { [key: string]: RuleWeight[] } = {
  gold: [
    // Generic gold exploration rules
    { ruleName: 'sericite_alteration', weight: 25, enabled: true, description: 'SWIR spectral signature of sericite alteration' },
    { ruleName: 'iron_oxide_alteration', weight: 20, enabled: true, description: 'Red/Blue ratio indicating iron oxide minerals' },
    { ruleName: 'gold_geochemical_anomaly', weight: 30, enabled: true, description: 'Elevated gold in stream sediments or soils' },
    { ruleName: 'fault_proximity_gold', weight: 20, enabled: true, description: 'Distance to mapped fault structures' },
    { ruleName: 'favorable_host_gold', weight: 15, enabled: true, description: 'Metamorphic or volcanic host rocks' },
    { ruleName: 'arsenic_pathfinder', weight: 15, enabled: false, description: 'Arsenic as pathfinder element for gold' },
    // Peru-specific epithermal gold rules (Daura Gold preset)
    { ruleName: 'peru_sericite_alteration', weight: 30, enabled: false, description: 'Peru epithermal sericite alteration (SWIR1/SWIR2 > 1.10)' },
    { ruleName: 'peru_argillic_alteration', weight: 25, enabled: false, description: 'Peru argillic alteration zones' },
    { ruleName: 'peru_silicification', weight: 28, enabled: false, description: 'Silicification associated with epithermal systems' },
    { ruleName: 'peru_gold_pathfinder', weight: 35, enabled: false, description: 'Peru gold pathfinder elements (As, Sb, Hg)' },
    { ruleName: 'peru_silver_association', weight: 20, enabled: false, description: 'Silver association with epithermal gold' },
    { ruleName: 'peru_arsenic_pathfinder', weight: 18, enabled: false, description: 'Arsenic pathfinder for Peru epithermals' },
    { ruleName: 'peru_mercury_volatiles', weight: 15, enabled: false, description: 'Mercury volatiles in epithermal systems' },
    { ruleName: 'ancash_structural_control', weight: 22, enabled: false, description: 'NW-SE structural control in Ancash' },
    { ruleName: 'pucajirca_volcanism', weight: 18, enabled: false, description: 'Miocene Pucajirca volcanic host rocks' }
  ],
  silver: [
    { ruleName: 'silver_geochemical_anomaly', weight: 30, enabled: true, description: 'Elevated silver in geochemical surveys' },
    { ruleName: 'clay_alteration', weight: 15, enabled: true, description: 'SWIR spectral signature of clay alteration' },
    { ruleName: 'fault_proximity_gold', weight: 20, enabled: true, description: 'Structural controls for epithermal systems' },
    { ruleName: 'iron_oxide_alteration', weight: 15, enabled: true, description: 'Iron oxide alteration halos' }
  ],
  copper: [
    { ruleName: 'copper_geochemical_anomaly', weight: 35, enabled: true, description: 'Elevated copper in geochemical data' },
    { ruleName: 'molybdenum_pathfinder', weight: 20, enabled: true, description: 'Molybdenum association in porphyry systems' },
    { ruleName: 'favorable_host_copper', weight: 20, enabled: true, description: 'Intrusive or porphyritic host rocks' },
    { ruleName: 'iron_oxide_alteration', weight: 15, enabled: false, description: 'Propylitic alteration zones' }
  ],
  peru_gold: [
    { ruleName: 'peru_sericite_alteration', weight: 30, enabled: true, description: 'Peru-specific sericite alteration (SWIR1/SWIR2 > 1.10)' },
    { ruleName: 'ancash_structural_control', weight: 25, enabled: true, description: 'Ancash fault proximity (<600m from structures)' },
    { ruleName: 'pucajirca_volcanism', weight: 20, enabled: true, description: 'Volcanic/andesitic host rocks (Pucajirca center)' },
    { ruleName: 'peru_argillic_alteration', weight: 25, enabled: true, description: 'Argillic alteration zones (clay minerals > 1.15)' },
    { ruleName: 'peru_silicification', weight: 28, enabled: true, description: 'Silicification zones (silica alteration > 1.20)' },
    { ruleName: 'peru_gold_pathfinder', weight: 35, enabled: true, description: 'Gold anomalies (>10 ppm for epithermal systems)' },
    { ruleName: 'peru_silver_association', weight: 20, enabled: true, description: 'Silver association (>5 ppm Ag)' },
    { ruleName: 'peru_arsenic_pathfinder', weight: 18, enabled: true, description: 'Arsenic pathfinder (>30 ppm As)' },
    { ruleName: 'peru_mercury_volatiles', weight: 15, enabled: false, description: 'Mercury volatiles (>100 ppb Hg)' }
  ]
};

interface MineralDiscoveryPanelProps {
  onAnalysisComplete?: (result: any) => void;
  selectedProject?: any;
  polygonRegions?: any[];
  gridResolutionResolver?: (context: { projectId?: string | null; polygonId?: string | null }) => number;
}

const MineralDiscoveryPanel: React.FC<MineralDiscoveryPanelProps> = ({
  onAnalysisComplete,
  selectedProject,
  polygonRegions = [],
  gridResolutionResolver
}) => {
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [selectedPolygon, setSelectedPolygon] = useState<string>(''); // polygonId = polygon (rectangular removed)
  const [configuration, setConfiguration] = useState<AnalysisConfiguration>({
    commodity: 'gold',
    region: 'South America',
    ruleWeights: DEFAULT_RULE_WEIGHTS.gold,
    analysisArea: {
      bounds: [-30, -75, -15, -45], // Not used in polygon-only mode
      gridResolution: 0.01 // ~1km resolution (legacy, replaced by smart grid)
    },
    outputFormats: ['geojson'],
    confidenceThreshold: 0.6,
    goldVeinAnalysis: true // Enable gold vein analysis by default for gold
  });
  
  // NEW: Smart grid resolution state
  const [explorationPhase, setExplorationPhase] = useState<'reconnaissance' | 'detailed' | 'precision' | 'custom'>('detailed');
  const [customGridSize, setCustomGridSize] = useState<number>(4); // Grid size (not degrees) - Max 5 for Earth Engine
  
  // Helper functions for smart grid resolution
  const getGridSizeForPhase = (phase: string): number => {
    switch (phase) {
      case 'reconnaissance': return 3;   // Fast overview (3x3 = 9 points)
      case 'detailed': return 4;        // Standard exploration (4x4 = 16 points)
      case 'precision': return 5;       // High-detail analysis (5x5 = 25 points) 
      default: return 4;
    }
  };
  
  const getEstimatedTime = (gridSize: number): string => {
    // Rough time estimates based on grid size
    const baseTime = Math.pow(gridSize / 10, 2) * 30; // Quadratic scaling
    if (baseTime < 60) return `~${Math.round(baseTime)}s`;
    if (baseTime < 3600) return `~${Math.round(baseTime / 60)}min`;
    return `~${Math.round(baseTime / 3600)}h`;
  };
  
  const getCurrentGridSize = useCallback((): number => {
    if (gridResolutionResolver) {
      return gridResolutionResolver({ projectId: selectedProject?.id || null, polygonId: selectedPolygon || null });
    }
    return explorationPhase === 'custom' ? customGridSize : getGridSizeForPhase(explorationPhase);
  }, [customGridSize, explorationPhase, gridResolutionResolver, selectedPolygon, selectedProject?.id]);
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>({
    id: '',
    status: 'idle' as const,
    progress: 0,
    message: 'Ready to start analysis'
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedAnalysisIds, setSavedAnalysisIds] = useState<Set<string>>(new Set());

  // Progress tracking via polling (WebSocket fallback removed)

  const steps = [
    'Select Analysis Type',
    'Configure Rules',
    'Define Analysis Area',
    'Run Analysis'
  ];

  // Progress tracking uses HTTP polling of the status endpoint

  // Handle preset selection
  const handlePresetSelect = useCallback((presetId: string) => {
    const preset = PRESET_CONFIGURATIONS.find(p => p.id === presetId);
    if (preset) {
      setSelectedPreset(presetId);
      setConfiguration(prev => ({
        ...prev,
        commodity: preset.commodity,
        region: preset.region,
        ruleWeights: DEFAULT_RULE_WEIGHTS[preset.commodity].map(rule => ({
          ...rule,
          enabled: preset.rules.includes(rule.ruleName)
        }))
      }));
    }
  }, []);

  // Handle rule weight changes
  const handleRuleWeightChange = useCallback((ruleName: string, newWeight: number) => {
    setConfiguration(prev => ({
      ...prev,
      ruleWeights: prev.ruleWeights.map(rule =>
        rule.ruleName === ruleName ? { ...rule, weight: newWeight } : rule
      )
    }));
  }, []);

  // Handle rule toggle
  const handleRuleToggle = useCallback((ruleName: string, enabled: boolean) => {
    setConfiguration(prev => ({
      ...prev,
      ruleWeights: prev.ruleWeights.map(rule =>
        rule.ruleName === ruleName ? { ...rule, enabled } : rule
      )
    }));
  }, []);

  // Function to save analysis as document
  const saveAnalysisAsDocument = useCallback(async (analysisResult: any) => {
    if (!selectedProject?.id) {
      return;
    }

    // Check if this analysis has already been saved
    if (savedAnalysisIds.has(analysisResult.id)) {
      return;
    }

    try {
      const analysisDate = new Date(analysisResult.analysisDate || new Date()).toLocaleDateString();
      const documentName = `${analysisResult.commodity?.toUpperCase() || 'Mineral'} Analysis - ${analysisResult.region || 'Unknown Region'} - ${analysisDate}`;
      
      // Create a formatted analysis summary
      const analysisContent = `# ${documentName}

## Analysis Summary
- **Commodity:** ${analysisResult.commodity?.toUpperCase() || 'Unknown'}
- **Region:** ${analysisResult.region || 'Unknown'}
- **Analysis Date:** ${analysisDate}
- **Analysis ID:** ${analysisResult.id}
- **Status:** ${analysisResult.status}

## Results Overview
- **Total Locations Analyzed:** ${analysisResult.results?.totalLocations || 0}
- **High Potential Locations:** ${analysisResult.results?.highPotentialLocations || 0}
- **Maximum Score:** ${analysisResult.results?.maxScore || 0}
- **Average Confidence:** ${((analysisResult.results?.averageConfidence || 0) * 100).toFixed(0)}%

## High Potential Areas
${(analysisResult.highPotentialAreas || []).map((area: any, index: number) => `
### Area ${index + 1}
- **Location:** ${area.coordinates?.lat?.toFixed(4)}°, ${area.coordinates?.lon?.toFixed(4)}°
- **Score:** ${area.score || 0}
- **Confidence:** ${((area.confidence || 0) * 100).toFixed(0)}%
- **Priority Rank:** ${area.priorityRank || 'N/A'}
- **Recommended Action:** ${area.recommendedAction || 'No specific action recommended'}
`).join('')}

## Analysis Configuration
- **Rules Applied:** ${configuration.ruleWeights.filter(r => r.enabled).map(r => r.ruleName.replace(/_/g, ' ')).join(', ')}
- **Confidence Threshold:** ${Math.round(configuration.confidenceThreshold * 100)}%
- **Grid Resolution:** ~${Math.round(configuration.analysisArea.gridResolution * 111)}km

---
*This analysis was generated automatically by the Mineral AI Platform*
`;

      const documentData = {
        name: documentName,
        type: 'analysis',
        category: 'mineral analysis',
        tags: ['mineral analysis', analysisResult.commodity?.toLowerCase() || 'mineral', analysisResult.region?.toLowerCase() || 'region', 'automated'],
        content: analysisContent,
        metadata: {
          analysisId: analysisResult.id,
          commodity: analysisResult.commodity,
          region: analysisResult.region,
          analysisDate: analysisResult.analysisDate,
          configuration: configuration,
          results: analysisResult.results,
          highPotentialAreas: analysisResult.highPotentialAreas
        }
      };

      const response = await fetch(`http://localhost:8000/api/projects/${selectedProject.id}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentData),
      });

      if (response.ok) {
        const savedDocument = await response.json();
        // Mark this analysis as saved to prevent duplicates
        setSavedAnalysisIds(prev => new Set(prev).add(analysisResult.id));
        
        // Trigger document refresh in DocumentView
        window.dispatchEvent(new CustomEvent('documentsUpdated'));
      } else {
        console.error('Failed to save analysis as document:', response.statusText);
      }
    } catch (error) {
      console.error('Error saving analysis as document:', error);
    }
  }, [selectedProject, configuration, savedAnalysisIds]);

  // Start analysis
  const handleStartAnalysis = useCallback(async () => {
    // Reset saved analysis IDs and logs for new analysis
    setSavedAnalysisIds(new Set());
    
    setAnalysisStatus({
      id: `analysis_${Date.now()}`,
      status: 'running' as const,
      progress: 0,
      message: 'Initializing analysis...',
      startTime: new Date()
    });

    // Trigger smart polling in App.tsx
    window.dispatchEvent(new CustomEvent('analysisStarted'));

    try {
      // Polygon-only analysis - ensure polygon is selected
      if (!selectedPolygon) {
        throw new Error('Please select a polygon region for analysis');
      }
      
      const polygon = polygonRegions.find(p => p.id === selectedPolygon);
      if (!polygon) {
        throw new Error('Selected polygon region not found');
      }
      
      const gridResolution = getCurrentGridSize();

      // Prepare request body for polygon analysis with smart grid resolution
      const requestBody = {
        commodity: configuration.commodity,
        region: configuration.region || selectedProject?.location || 'South America',
        polygon_coordinates: polygon.coordinates,
        polygon_id: polygon.id,
        polygon_name: polygon.name,
        analysis_name: `${configuration.commodity} analysis for ${polygon.name}`,
        confidence_threshold: configuration.confidenceThreshold,
        output_formats: configuration.outputFormats,
        vein_analysis: configuration.goldVeinAnalysis && configuration.commodity === 'gold',
        project_id: selectedProject?.id,
        manual_trigger: true,
        // NEW: Pass grid resolution to backend
        grid_resolution: gridResolution,
        exploration_phase: explorationPhase,
        analysis_config: {
          grid: {
            default_grid_resolution: gridResolution
          }
        }
      };
      
      // Enhanced debug logging for polygon analysis flow
      // 🎯 CRITICAL DEBUG: Show project_id being sent
      const response = await fetch('http://localhost:8000/api/enhanced-analysis/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🚨 API Response Error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Analysis failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      // 🔍 DEBUG: Check if analysisId is received correctly
      if (!result.analysisId) {
        console.error('❌ No analysisId received from API response!');
        throw new Error('No analysis ID received from server');
      }
      
      setAnalysisStatus(prev => ({
        ...prev,
        id: result.analysisId,
        message: 'Analysis started successfully - polling for progress...'
      }));

      // 🔍 DEBUG: Confirm polling is starting
      // Fallback progress simulation in case status endpoint fails
      let fallbackProgress = 5;
      const fallbackInterval = setInterval(() => {
        setAnalysisStatus(prev => {
          if (prev.status === 'running' && prev.progress < 90) {
            const newProgress = Math.min(prev.progress + 5, 90);
            return {
              ...prev,
              progress: newProgress,
              message: `Analysis ${newProgress}% complete (estimated)`
            };
          }
          return prev;
        });
      }, 3000); // Update every 3 seconds as fallback

      // Proper polling mechanism using the status endpoint
      const pollInterval = setInterval(async () => {
        try {
          // Fetch status
          const statusResponse = await fetch(`http://localhost:8000/api/mineral-discovery/analysis/${result.analysisId}/status`);

          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            
            // 🔍 DEBUG: Show status response details
            setAnalysisStatus(prev => ({
              ...prev,
              progress: statusData.progress || 0,
              message: statusData.status === 'failed' ? 'Analysis completed but failed - no real data available' : `Analysis ${statusData.progress || 0}% complete`,
              status: statusData.status === 'completed' ? 'completed' : statusData.status === 'failed' ? 'failed' : 'running',
              estimatedTimeRemaining: statusData.estimatedTimeRemaining
            }));
            
            // If analysis is complete OR failed (but reached 100%), fetch results and stop polling
            if (statusData.status === 'completed' || statusData.status === 'failed') {
              clearInterval(pollInterval);
              clearInterval(fallbackInterval); // Also clear fallback progress
              
              if (onAnalysisComplete) {
                try {
                  const resultsResponse = await fetch(`http://localhost:8000/api/mineral-discovery/results/${result.analysisId}`);
                  if (resultsResponse.ok) {
                    const analysisResults = await resultsResponse.json();
                    
                    // Enhanced debug logging for polygon analysis results
                    if (analysisResults.results?.analysisType === 'polygon') {
                      // Log each high potential area with polygon-specific info
                      analysisResults.highPotentialAreas?.forEach((area: any, index: number) => {
                        if (area.analysis_region) {
                          }
                      });
                    }

                    // 🚨 CRITICAL DEBUG: Check callback before invoking
                    if (onAnalysisComplete) {
                      onAnalysisComplete(analysisResults);
                      } else {
                      console.error('❌ CRITICAL: onAnalysisComplete callback is undefined!');
                    }
                    // Auto-save as document
                    await saveAnalysisAsDocument(analysisResults);
                  }
                } catch (error) {
                  // For failed analyses, create an error report instead of synthetic data
                  if (statusData.status === 'failed') {
                    const errorResult = {
                      id: result.analysisId,
                      commodity: configuration.commodity,
                      region: configuration.region,
                      analysisDate: new Date().toISOString(),
                      status: 'failed',
                      error: statusData.error || 'Analysis failed - real data unavailable',
                      message: 'Analysis completed but failed to generate results due to service unavailability. No synthetic data was generated per project policy.',
                      results: {
                        totalLocations: 0,
                        highPotentialLocations: 0,
                        averageScore: 0,
                        maxScore: 0,
                        averageConfidence: 0,
                        error: 'Real mineral analysis failed and no synthetic data allowed'
                      },
                      highPotentialAreas: [],
                      requiresAuthentication: true,
                      nextSteps: [
                        'Configure Google Earth Engine authentication',
                        'Set up USGS M2M API credentials', 
                        'Ensure OpenAI API key is configured',
                        'Retry analysis with proper service authentication'
                      ]
                    };
                    onAnalysisComplete(errorResult);
                    // Save error report as document
                    await saveAnalysisAsDocument(errorResult);
                  }
                }
                window.dispatchEvent(new CustomEvent('analysisCompleted'));
              }
            }
          } else {
            // 🚨 DEBUG: Status request failed
            console.error('❌ Status request failed:', {
              status: statusResponse.status,
              statusText: statusResponse.statusText,
              url: statusResponse.url
            });
          }
        } catch (error) {
          console.error('❌ Failed to poll analysis status:', error);
        }
      }, 2000); // Poll every 2 seconds
      
      // Stop polling after 40 seconds (backend completes in 30s)
      setTimeout(() => {
        clearInterval(pollInterval);
        clearInterval(fallbackInterval);
      }, 40000);

    } catch (error) {
      setAnalysisStatus(prev => ({
        ...prev,
        status: 'failed' as const,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));
    }
  }, [configuration, explorationPhase, getCurrentGridSize, selectedPolygon, polygonRegions, selectedProject, onAnalysisComplete, saveAnalysisAsDocument]);

  // Stop analysis
  const handleStopAnalysis = useCallback(async () => {
    if (analysisStatus.id) {
      try {
        await fetch(`http://localhost:8000/api/mineral-discovery/stop-analysis/${analysisStatus.id}`, {
          method: 'POST'
        });
        
        setAnalysisStatus(prev => ({
          ...prev,
          status: 'idle' as const,
          message: 'Analysis stopped by user'
        }));
      } catch (error) {
        console.error('Failed to stop analysis:', error);
      }
    }
  }, [analysisStatus.id]);

  // Render preset selection step
  const renderPresetSelection = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
        Choose Analysis Type
      </Typography>
      <Typography variant="body2" sx={{ 
        mb: 3, 
        color: 'text.primary', 
        fontSize: '0.95rem',
        lineHeight: 1.6,
        fontWeight: 500
      }}>
        Select a preset configuration optimized for specific commodities and regions, or start with a custom configuration.
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {PRESET_CONFIGURATIONS.map((preset) => {
          const isSelected = selectedPreset === preset.id;
          return (
            <Box
              key={preset.id}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                padding: '12px 16px',
                borderRadius: '8px',
                border: isSelected ? '2px solid #2196f3' : '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.35)' : '#0D1117',
                boxShadow: isSelected ? '0 0 0 4px rgba(33, 150, 243, 0.2), inset 0 0 20px rgba(33, 150, 243, 0.15)' : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.35)' : 'rgba(33, 150, 243, 0.1)',
                  borderColor: '#2196f3'
                }
              }}
              onClick={() => handlePresetSelect(preset.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ 
                  color: isSelected ? '#ffffff' : 'text.primary', 
                  fontWeight: isSelected ? 700 : 600,
                  fontSize: '0.9rem'
                }}>
                  {preset.name}
                </Typography>
                <Chip 
                  label={preset.commodity.toUpperCase()} 
                  size="small" 
                  sx={{ 
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: isSelected ? 600 : 400,
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'default'
                  }}
                  color={preset.commodity === 'gold' ? 'warning' : 
                         preset.commodity === 'silver' ? 'default' : 
                         preset.commodity === 'copper' ? 'info' : 'default'}
                />
              </Box>
              <Typography variant="caption" sx={{ 
                color: isSelected ? 'rgba(255, 255, 255, 0.95)' : 'text.primary', 
                opacity: isSelected ? 1 : 0.7,
                fontSize: '0.75rem',
                lineHeight: 1.3,
                display: 'block'
              }}>
                {preset.description}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedPreset('custom');
            setActiveStep(1);
          }}
        >
          Start Custom Configuration
        </Button>
        
        {selectedPreset && selectedPreset !== 'custom' && (
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={() => setActiveStep(1)}
          >
            Use Selected Preset
          </Button>
        )}
      </Box>
    </Box>
  );

  // Render rule configuration step
  const renderRuleConfiguration = () => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ 
          color: 'text.primary', 
          fontWeight: 700,
          fontSize: '1.3rem',
          textShadow: '0px 1px 2px rgba(0,0,0,0.1)'
        }}>
          Configure Rules for {configuration.commodity.toUpperCase()}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showAdvanced}
              onChange={(e) => setShowAdvanced(e.target.checked)}
            />
          }
          label="Advanced Mode"
          sx={{ '& .MuiFormControlLabel-label': { color: 'text.primary', fontWeight: 500 } }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'text.primary' }}>Commodity</InputLabel>
          <Select
            value={configuration.commodity}
            onChange={(e) => {
              const commodity = e.target.value as 'gold' | 'silver' | 'copper';
              setConfiguration(prev => ({
                ...prev,
                commodity,
                ruleWeights: DEFAULT_RULE_WEIGHTS[commodity],
                goldVeinAnalysis: commodity === 'gold' // Enable gold vein analysis only for gold
              }));
            }}
          >
            <MenuItem value="gold" sx={{ color: 'text.primary' }}>Gold</MenuItem>
            <MenuItem value="silver" sx={{ color: 'text.primary' }}>Silver</MenuItem>
            <MenuItem value="copper" sx={{ color: 'text.primary' }}>Copper</MenuItem>
          </Select>
        </FormControl>

        {/* Gold Vein Analysis Toggle - only show for gold */}
        {configuration.commodity === 'gold' && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={configuration.goldVeinAnalysis}
                  onChange={(e) => setConfiguration(prev => ({
                    ...prev,
                    goldVeinAnalysis: e.target.checked
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Enhanced Gold Vein Analysis
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6, display: 'block' }}>
                    Uses advanced spectral indices for gold vein detection including quartz signature, 
                    advanced sericite alteration, gossan zones, and vein linearity analysis
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
        Rule Weights and Configuration
      </Typography>

      {configuration.ruleWeights.map((rule) => (
        <Paper key={rule.ruleName} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rule.enabled}
                    onChange={(e) => handleRuleToggle(rule.ruleName, e.target.checked)}
                  />
                }
                label={rule.ruleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                sx={{ '& .MuiFormControlLabel-label': { color: 'text.primary', fontWeight: 500 } }}
              />
              <Tooltip title={rule.description}>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Chip 
              label={`Weight: ${rule.weight}`} 
              size="small" 
              color={rule.enabled ? 'primary' : 'default'}
            />
          </Box>
          
          {rule.enabled && (
            <Box sx={{ px: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', opacity: 0.7 }}>
                {rule.description}
              </Typography>
              {showAdvanced && (
                <Slider
                  value={rule.weight}
                  onChange={(_, value) => handleRuleWeightChange(rule.ruleName, value as number)}
                  min={0}
                  max={50}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' }
                  ]}
                  valueLabelDisplay="auto"
                />
              )}
            </Box>
          )}
        </Paper>
      ))}

      {showAdvanced && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            Advanced Settings
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ color: 'text.primary', fontWeight: 500 }}>
              Confidence Threshold: {configuration.confidenceThreshold}
            </Typography>
            <Slider
              value={configuration.confidenceThreshold}
              onChange={(_, value) => setConfiguration(prev => ({
                ...prev,
                confidenceThreshold: value as number
              }))}
              min={0.1}
              max={1.0}
              step={0.05}
              marks={[
                { value: 0.1, label: '10%' },
                { value: 0.5, label: '50%' },
                { value: 0.9, label: '90%' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );

  // Render analysis area step - Polygon Only
  const renderAnalysisArea = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
        Select Polygon Region
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Select a drawn polygon region for mineral analysis. Rectangular analysis has been removed to focus on precise polygon-based exploration.
      </Alert>
      
      {/* Polygon Selection */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
          Available Polygon Regions
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'text.primary' }}>Select Polygon Region</InputLabel>
          <Select
            value={selectedPolygon}
            onChange={(e) => setSelectedPolygon(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <Typography sx={{ color: 'text.primary', opacity: 0.6 }}>Please select a polygon region...</Typography>
            </MenuItem>
            {polygonRegions.filter(p => p.is_valid).map((polygon) => (
              <MenuItem key={polygon.id} value={polygon.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        backgroundColor: '#9333ea', 
                        mr: 1, 
                        borderRadius: '2px' 
                      }} 
                    />
                    {polygon.name}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                    {polygon.metadata?.area_km2 ? `${(polygon.metadata.area_km2 / 1000).toFixed(1)}k km²` : 'Unknown area'}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!selectedPolygon && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please select a polygon region to continue. You can draw new polygons on the map if needed.
          </Alert>
        )}

        {selectedPolygon && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Selected polygon: {polygonRegions.find(p => p.id === selectedPolygon)?.name}
          </Alert>
        )}
      </Paper>

      {/* Selected Polygon Details */}
      {selectedPolygon && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            Polygon Analysis Details
          </Typography>
          {(() => {
            const polygon = polygonRegions.find(p => p.id === selectedPolygon);
            if (!polygon) return <Typography sx={{ color: 'text.primary', opacity: 0.6 }}>Polygon not found</Typography>;
            
            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box 
                    sx={{ 
                      width: 16, 
                      height: 16, 
                      backgroundColor: '#9333ea', 
                      mr: 1, 
                      borderRadius: '2px' 
                    }} 
                  />
                  <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>{polygon.name}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Area: {polygon.metadata?.area_km2 ? `${(polygon.metadata.area_km2 / 1000).toFixed(1)}k km²` : 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Vertices: {polygon.metadata?.vertex_count || polygon.coordinates?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Bounds: {polygon.metadata?.bounds ? 
                      `${polygon.metadata.bounds.min_lat.toFixed(2)}° to ${polygon.metadata.bounds.max_lat.toFixed(2)}°` : 
                      'Unknown'
                    }
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Perimeter: {polygon.metadata?.perimeter_km ? `${polygon.metadata.perimeter_km.toFixed(0)} km` : 'Unknown'}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Paper>
      )}

      {/* Smart Grid Resolution Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
          Analysis Resolution & Performance
        </Typography>
        
        {/* Exploration Phase Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.primary', fontWeight: 500 }}>
            Exploration Phase
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { id: 'reconnaissance', label: 'Reconnaissance', desc: 'Fast Overview', grid: 15, color: 'success' },
              { id: 'detailed', label: 'Detailed', desc: 'Standard Analysis', grid: 25, color: 'primary' },
              { id: 'precision', label: 'High-Precision', desc: 'Detailed Mapping', grid: 50, color: 'warning' }
            ].map((phase) => (
              <Card 
                key={phase.id}
                sx={{ 
                  cursor: 'pointer',
                  border: explorationPhase === phase.id ? '2px solid #2196f3' : '1px solid rgba(255,255,255,0.1)',
                  backgroundColor: explorationPhase === phase.id ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                  transition: 'all 0.2s ease',
                  minWidth: 140,
                  '&:hover': { 
                    backgroundColor: 'rgba(33, 150, 243, 0.05)',
                    borderColor: '#2196f3'
                  }
                }}
                onClick={() => setExplorationPhase(phase.id as any)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                    {phase.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, display: 'block', mb: 1 }}>
                    {phase.desc}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip 
                      label={`${phase.grid}×${phase.grid}`} 
                      size="small" 
                      color={phase.color as any}
                      sx={{ fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                      {getEstimatedTime(phase.grid)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Current Selection Summary */}
        <Box sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.05)', borderRadius: 1, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Selected Configuration
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, textAlign: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, display: 'block' }}>
                Grid Size
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {getCurrentGridSize()}×{getCurrentGridSize()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, display: 'block' }}>
                Sample Points
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                ~{getCurrentGridSize() * getCurrentGridSize()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, display: 'block' }}>
                Est. Time
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {getEstimatedTime(getCurrentGridSize())}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Advanced Custom Resolution */}
        {showAdvanced && (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={explorationPhase === 'custom'}
                  onChange={(e) => setExplorationPhase(e.target.checked ? 'custom' : 'detailed')}
                />
              }
              label="Custom Grid Resolution"
              sx={{ '& .MuiFormControlLabel-label': { color: 'text.primary', fontWeight: 500 } }}
            />
            
            {explorationPhase === 'custom' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ color: 'text.primary', fontWeight: 500 }}>
                  Custom Grid Size: {customGridSize}×{customGridSize} (~{customGridSize * customGridSize} points)
                </Typography>
                <Slider
                  value={customGridSize}
                  onChange={(_, value) => setCustomGridSize(value as number)}
                  min={3}
                  max={5}
                  step={1}
                  marks={[
                    { value: 3, label: '3×3' },
                    { value: 4, label: '4×4' },
                    { value: 5, label: '5×5' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}×${value}`}
                />
                <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, mt: 1, display: 'block' }}>
                  Estimated Time: {getEstimatedTime(customGridSize)} | 
                  Memory Usage: {customGridSize === 5 ? 'Medium' : 'Low'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );

  // Render analysis execution step
  const renderAnalysisExecution = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
        Run Analysis
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary', fontWeight: 600 }}>
          Analysis Configuration Summary
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={`Commodity: ${configuration.commodity.toUpperCase()}`} />
          <Chip label={`Region: ${configuration.region}`} />
          <Chip label={`Active Rules: ${configuration.ruleWeights.filter(r => r.enabled).length}`} />
          <Chip label={`Confidence: ${Math.round(configuration.confidenceThreshold * 100)}%`} />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            Analysis Status
          </Typography>
          <Box>
            {analysisStatus.status === 'running' ? (
              <Button
                startIcon={<StopIcon />}
                onClick={handleStopAnalysis}
                color="error"
                variant="outlined"
                size="small"
              >
                Stop
              </Button>
            ) : (
              <Button
                startIcon={<RunIcon />}
                onClick={handleStartAnalysis}
                color="primary"
                variant="contained"
                disabled={analysisStatus.status !== 'idle' && analysisStatus.status !== 'failed'}
              >
                Start Analysis
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', opacity: 0.8 }}>
            {analysisStatus.message}
          </Typography>
          
          {analysisStatus.status === 'running' && (
            <>
              <LinearProgress 
                variant="determinate" 
                value={analysisStatus.progress} 
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7 }}>
                Progress: {Math.round(analysisStatus.progress)}%
                {analysisStatus.estimatedTimeRemaining && (
                  ` • Est. ${Math.round(analysisStatus.estimatedTimeRemaining / 60000)} min remaining`
                )}
              </Typography>
            </>
          )}
        </Box>

        {analysisStatus.status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Analysis completed successfully! Results are ready for review.
          </Alert>
        )}

        {analysisStatus.status === 'failed' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {analysisStatus.message}
          </Alert>
        )}
      </Paper>
      
    </Box>
  );

  return (
    <Paper sx={{ 
      p: 3, 
      height: '100%', 
      overflow: 'auto',
      bgcolor: 'background.paper',
      color: 'text.primary',
      '& .MuiTypography-root': {
        color: 'text.primary'
      },
      '& .MuiFormControlLabel-label': {
        color: 'text.primary'
      },
      '& .MuiInputLabel-root': {
        color: 'text.primary'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ 
          color: 'text.primary', 
          fontWeight: 700,
          fontSize: '1.75rem',
          textShadow: '0px 1px 3px rgba(0,0,0,0.2)'
        }}>
          Mineral Discovery Analysis
        </Typography>
        <Box>
          <Tooltip title="Save Configuration">
            <IconButton onClick={() => setSaveDialogOpen(true)}>
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Load Configuration">
            <IconButton>
              <LoadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel sx={{ '& .MuiStepLabel-label': { color: 'text.primary', fontWeight: 500 } }}>
              {label}
            </StepLabel>
            <StepContent>
              {index === 0 && renderPresetSelection()}
              {index === 1 && renderRuleConfiguration()}
              {index === 2 && renderAnalysisArea()}
              {index === 3 && renderAnalysisExecution()}
              
              <Box sx={{ mt: 2 }}>
                <Button
                  disabled={index === 0}
                  onClick={() => setActiveStep(index - 1)}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(index + 1)}
                  disabled={index === steps.length - 1 || (index === 2 && !selectedPolygon)}
                >
                  {index === steps.length - 1 ? 'Finish' : 'Continue'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Save Configuration Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Analysis Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            Save your current analysis configuration for future use.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MineralDiscoveryPanel;
